// Copyright (c) 2024 Merit International Inc. All Rights Reserved.

import { create } from "zustand";
import { getLocalConfig } from "@src/utils";
import { merge } from "lodash";
import type { Configuration } from "@src/configuration";

export type FeatureFlagNamesToValues = Configuration["featureFlags"]["namesToDefaultValues"];

const config = getLocalConfig();

type FeatureFlagsData = {
  readonly featureFlags: FeatureFlagNamesToValues;
};

type FeatureFlagsFunctions = {
  readonly getFeatureFlag: <T extends keyof FeatureFlagNamesToValues>(
    name: T
  ) => FeatureFlagNamesToValues[T];
  readonly upsertFeatureFlags: (featureFlags: FeatureFlagNamesToValues) => void;
  readonly upsertFeatureFlag: <T extends keyof FeatureFlagNamesToValues>(
    name: T,
    value: FeatureFlagNamesToValues[T]
  ) => void;
};

const initialState: FeatureFlagsData = {
  featureFlags: config.featureFlags.namesToDefaultValues,
};

export const useFeatureFlagsStore = create<FeatureFlagsData & FeatureFlagsFunctions>()(
  (set, get) => ({
    ...initialState,

    getFeatureFlag: name => get().featureFlags[name],
    upsertFeatureFlag: (name, value) => {
      set(state => ({
        ...state,
        featureFlags: {
          ...state.featureFlags,
          [name]: value,
        },
      }));
    },
    upsertFeatureFlags: featureFlags => {
      set(store => ({ featureFlags: merge({}, store.featureFlags, featureFlags) }));
    },
  })
);
